import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useContent } from "../context/ContentContext";

const Navbar = () => {
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const items = content?.nav?.items || [];
  const coupleNames = content?.settings?.coupleNames || "George & Amy";

  // Pages that open with a dark hero image need white nav text until the user scrolls.
  const overDark = location.pathname === "/details" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const go = (path) => {
    const [base, hash] = path.split("#");
    if (base && base !== location.pathname) {
      navigate(base);
      if (hash) setTimeout(() => scrollToId(hash), 400);
    } else if (hash) {
      scrollToId(hash);
    } else {
      navigate(base || "/");
    }
    setMobileOpen(false);
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-offwhite/80 backdrop-blur-xl shadow-[0_8px_30px_rgba(38,62,76,0.06)] py-3" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className={`font-serif text-xl md:text-2xl tracking-tight transition-colors duration-300 ${overDark ? "text-white" : "text-navy"}`} data-testid="nav-logo">
          {coupleNames}
        </Link>

        {/* Desktop */}
        <ul className="hidden lg:flex items-center gap-8">
          {items.map((item) => (
            <li
              key={item.path}
              className="relative group"
              onMouseEnter={() => setOpenSub(item.path)}
              onMouseLeave={() => setOpenSub(null)}
            >
              <button
                onClick={() => go(item.path)}
                className={`flex items-center gap-1 text-sm uppercase tracking-[0.18em] transition-colors duration-300 ${overDark ? "text-white/90 hover:text-white" : "text-navy/80 hover:text-dusty"}`}
                data-testid={`nav-link-${item.path.replace(/[^a-z]/gi, "")}`}
              >
                {item.label}
                {item.children?.length > 0 && <ChevronDown size={13} className="mt-0.5" />}
              </button>
              <AnimatePresence>
                {item.children?.length > 0 && openSub === item.path && (
                  <motion.ul
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 top-full pt-4 w-56"
                  >
                    <div className="bg-white rounded-lg shadow-[0_20px_50px_rgba(38,62,76,0.12)] border border-border overflow-hidden py-2">
                      {item.children.map((c) => (
                        <li key={c.path}>
                          <button
                            onClick={() => go(c.path)}
                            className="w-full text-left px-5 py-2.5 text-sm text-navy/80 hover:bg-offwhite hover:text-dusty transition-colors"
                            data-testid={`nav-sub-${c.path.replace(/[^a-z]/gi, "")}`}
                          >
                            {c.label}
                          </button>
                        </li>
                      ))}
                    </div>
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>
          ))}
          <li>
            <button
              onClick={() => go("/rsvp")}
              className="ml-2 rounded-full bg-navy text-white text-xs uppercase tracking-[0.18em] px-6 py-3 hover:bg-dusty transition-colors duration-300"
              data-testid="nav-rsvp-cta"
            >
              RSVP
            </button>
          </li>
        </ul>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden transition-colors duration-300 ${overDark ? "text-white" : "text-navy"}`}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-offwhite/95 backdrop-blur-xl border-t border-border mt-3"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-4 flex flex-col gap-1">
              {items.map((item) => (
                <li key={item.path} className="py-1">
                  <button
                    onClick={() => go(item.path)}
                    className="w-full text-left font-serif text-2xl text-navy py-2"
                  >
                    {item.label}
                  </button>
                  {item.children?.length > 0 && (
                    <div className="pl-4 flex flex-col">
                      {item.children.map((c) => (
                        <button key={c.path} onClick={() => go(c.path)} className="text-left text-sm text-navy/70 py-1.5">
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
              <li className="pt-3">
                <button
                  onClick={() => go("/rsvp")}
                  className="w-full rounded-full bg-navy text-white text-sm uppercase tracking-[0.18em] px-6 py-4 hover:bg-dusty transition-colors duration-300"
                  data-testid="mobile-rsvp-cta"
                >
                  RSVP
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
